import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import ApiService from '../models/ApiService';

const ImportLesson = ({ onComplete }) => {
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [fileData, setFileData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [validationErrors, setValidationErrors] = useState([]);
    const [isPreview, setIsPreview] = useState(false);

    const requiredSheets = ['Lesson', 'Introduction', 'Teaching', 'Glossary', 'Question', 'SoundTable', 'Group', 'Notes', 'Summary', 'Symbol', 'VowelSound', 'DotTable', 'Variants', 'Compound', 'WordTable'];

    const validateWorkbook = (workbook) => {
        const errors = [];
        const sheetNames = workbook.SheetNames;

        // Check for required sheets
        requiredSheets.forEach(sheet => {
            if (!sheetNames.includes(sheet)) {
                errors.push(`Missing required sheet: ${sheet}`);
            }
        });

        // Check each sheet has data
        sheetNames.forEach(sheet => {
            const worksheet = workbook.Sheets[sheet];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            if (jsonData.length <= 1) { // 1 for header row
                errors.push(`Sheet "${sheet}" has no data rows`);
            }
        });

        return errors;
    };

    const processSheet = (worksheet) => {
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        // Remove empty rows
        return jsonData.filter(row => row.length > 0 && !row.every(cell => cell === ''));
    };

    const onDrop = useCallback((acceptedFiles) => {
        setError('');
        setSuccess('');
        setValidationErrors([]);
        setFileData(null);
        setIsPreview(false);

        if (acceptedFiles.length === 0) {
            setError('Please upload a valid Excel file (.xlsx, .xls)');
            return;
        }

        const uploadedFile = acceptedFiles[0];
        setFile(uploadedFile);
        setFileName(uploadedFile.name);

        // Read file for preview
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });

                // Validate workbook structure
                const errors = validateWorkbook(workbook);
                if (errors.length > 0) {
                    setValidationErrors(errors);
                    return;
                }

                // Process all sheets for preview
                const processedData = {};
                workbook.SheetNames.forEach(sheetName => {
                    processedData[sheetName] = processSheet(workbook.Sheets[sheetName]);
                });

                setFileData(processedData);
                setIsPreview(true);
            } catch (err) {
                console.error('Error processing file:', err);
                setError(err.response.data.message);
            }
        };
        reader.readAsArrayBuffer(uploadedFile);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
        },
        multiple: false
    });

    const handleSubmit = async () => {
        if (!file) {
            setError('Please select a file first');
            return;
        }

        setIsLoading(true);
        setError('');
        setSuccess('');
        setValidationErrors([]); // Clear previous validation errors if any

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await ApiService.ImportLesson(formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setSuccess(response.data.message || 'Lesson data imported successfully!');
            setIsPreview(false);
            setFile(null); // ✅ Clear the file state

            if (onComplete) {
                onComplete(response.data);
            }
        } catch (error) {
            console.error('Import failed:', error);
            if (error.response) {
                if (error.response.status === 400) {
                    setValidationErrors([error.response.data.message || 'Invalid file format']);
                } else {
                    setError(error.response.data.message || 'Failed to import lesson data');
                }
            } else {
                setError('Network error. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };


    const renderPreviewTable = (sheetName, data) => {
        if (!data || data.length === 0) return null;

        const [headers, ...rows] = data;
        const previewRows = rows.slice(0, 5); // Show first 5 rows for preview

        return (
            <div key={sheetName} className="mt-4">
                <h3 className="font-medium text-gray-700 mb-2">{sheetName} (first 5 rows):</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 border">
                        <thead className="bg-gray-50">
                            <tr>
                                {headers.map((header, idx) => (
                                    <th key={idx} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {header || `Column ${idx + 1}`}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {previewRows.map((row, rowIdx) => (
                                <tr key={rowIdx}>
                                    {row.map((cell, cellIdx) => (
                                        <td key={cellIdx} className="px-3 py-2 text-sm text-gray-500">
                                            {cell}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="p-4 bg-white rounded-lg shadow-md">
            <h2 className="text-xl font-semibold text-[--text-color] mb-4">Import Lesson from Excel</h2>

            <div className="mb-4 p-3 bg-blue-50 rounded-md">
                <p className="text-sm text-gray-700">
                    <strong>Note:</strong> Your Excel file must include these sheets: {requiredSheets.join(', ')}.

                </p>
            </div>

            <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-[--border-color-o]'
                    }`}
            >
                <input {...getInputProps()} />
                {isLoading ? (
                    <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
                        <p className="text-gray-600">Processing file...</p>
                    </div>
                ) : (
                    <>
                        <div className="flex justify-center mb-2">
                            <svg className="w-10 h-10 text-[--text-color]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                            </svg>
                        </div>
                        {isDragActive ? (
                            <p className="text-blue-500">Drop the Excel file here</p>
                        ) : (
                            <>
                                <p className="text-gray-600">Drag & drop an Excel file here, or click to select</p>
                                <p className="text-sm text-gray-500 mt-1">Supports .xlsx, .xls files</p>
                            </>
                        )}
                    </>
                )}
            </div>

            {fileName && (
                <div className="mt-4 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm font-medium text-gray-700">Selected file: <span className="text-[--text-color]">{fileName}</span></p>
                </div>
            )}

            {validationErrors.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 rounded-md">
                    <h3 className="font-medium text-red-600 mb-2">File validation errors:</h3>
                    <ul className="list-disc list-inside text-sm text-red-600">
                        {validationErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                        ))}
                    </ul>
                </div>
            )}

            {error && (
                <div className="mt-4 p-3 bg-red-50 rounded-md">
                    <p className="text-sm font-medium text-red-600">{error}</p>
                </div>
            )}

            {success && (
                <div className="mt-4 p-3 bg-green-50 rounded-md">
                    <p className="text-sm font-medium text-green-600">{success}</p>
                </div>
            )}

            {isPreview && fileData && (
                <div className="mt-6">
                    <h3 className="font-medium text-gray-700 mb-2">File Preview:</h3>
                    {Object.keys(fileData).map(sheet => renderPreviewTable(sheet, fileData[sheet]))}
                </div>
            )}

            {file && (
                <div className="mt-6">
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="mt-4 w-full bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? 'Importing...' : 'Import Lesson Data'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ImportLesson;